import { AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ApiError, Congress } from "@/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import { queryClient } from "@/libs/react-query";
import { cn } from "@/utils";
import { zodResolver } from "@hookform/resolvers/zod";

import { congressMetaQueryKey } from "../api/get-congress";
import { useUpdateCongress } from "../api/update-congress";
import {
  AbstractDetailsFormData,
  abstractDetailsSchema,
} from "../types/abstract-details-schema";

import MacrosPopover from "./macros-popover";

type AbstractDetailsFormProps = {
  onSuccess?: () => void;
  initialData?: Congress;
};

function transformApiToFormData(congress?: Congress): AbstractDetailsFormData {
  return {
    abstractLeftTemplate: congress?.abstractLeftTemplate ?? "",
    abstractRightTemplate: congress?.abstractRightTemplate ?? "",
    isAbstractRatingEnabled: congress?.isAbstractRatingEnabled ?? false,
    isAbstractQaEnabled: congress?.isAbstractQaEnabled ?? false,
  };
}

const macros = [
  { name: "ID", code: "abstract_num" },
  { name: "Rank", code: "rank" },
  { name: "Title", code: "title" },
  { name: "Presenter(s)", code: "presenter" },
  { name: "Author(s)", code: "name_head" },
  { name: "Author institution(s)", code: "inst_head" },
  { name: "Lecture name", code: "lect_name" },
  { name: "Lecture number", code: "lect_number" },
  { name: "Lecture time", code: "lect_time" },
  { name: "Discussion time", code: "discussion_time" },
  { name: "Start Date", code: "date" },
  { name: "End Date", code: "date_end" },
  { name: "Start Time", code: "start_time" },
  { name: "End Time", code: "end_time" },
];

export function AbstractDetailsForm(props: AbstractDetailsFormProps) {
  const { t } = useTranslation();
  const { initialData } = props;
  const form = useForm<AbstractDetailsFormData>({
    resolver: zodResolver(abstractDetailsSchema),
    defaultValues: transformApiToFormData(initialData),
  });

  const { mutateAsync: updateCongress, isSuccess: isUpdateSuccess } =
    useUpdateCongress(initialData ? initialData.id : "");
  const {
    congress: { id: congressId },
  } = useCongress();

  useEffect(() => {
    if (isUpdateSuccess) {
      queryClient.invalidateQueries({
        queryKey: congressMetaQueryKey(congressId),
      });
    }
  }, [isUpdateSuccess, congressId]);

  const isEdit = !!initialData;

  const handleUpdateCongress = async (data: AbstractDetailsFormData) => {
    try {
      const congress = await updateCongress(data);
      form.reset(transformApiToFormData(congress));
    } catch (error) {
      if (error instanceof ApiError) {
        form.setError("root", {
          type: "manual",
          // @ts-expect-error body is not typed
          message: Array.isArray(error.body?.message)
            ? // @ts-expect-error body is not typed
              error.body?.message.join(", ")
            : // @ts-expect-error body is not typed
              error.body?.message,
        });
      }
    }
  };
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleUpdateCongress)}>
        <div className="space-y-6">
          <FormField
            control={form.control}
            name="abstractLeftTemplate"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t("detailsForm.leftColumn")}{" "}
                  <MacrosPopover macros={macros} />
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="A-%abstractnum%"
                    className="h-8 px-2 py-1"
                    disabled={form.formState.isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t("detailsForm.templateHelp")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="abstractRightTemplate"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t("detailsForm.rightColumn")}{" "}
                  <MacrosPopover macros={macros} />
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="A-%abstractnum%"
                    className="h-8 px-2 py-1"
                    disabled={form.formState.isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t("detailsForm.templateHelp")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid gap-4 lg:grid-cols-2">
            <FormField
              control={form.control}
              name="isAbstractRatingEnabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {t("detailsForm.enableRating")}
                    </FormLabel>
                    <FormDescription>
                      {t("detailsForm.abstractsRateable")}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {/* <FormField
              control={form.control}
              name="isAbstractQaEnabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Q&A engedélyezése
                    </FormLabel>
                    <FormDescription>
                      Az absztraktok Q&A funkciója engedélyezve
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            /> */}
          </div>

          {form.formState.errors.root && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertTitle>{t("common.errorTitle")}</AlertTitle>
              <AlertDescription>
                {form.formState.errors.root.message}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div
          className={cn(
            "mt-6 flex sm:justify-between",
            isEdit && "sm:justify-end",
          )}
        >
          {!isEdit && (
            <Button type="button" variant="secondary" onClick={props.onSuccess}>
              {t("congressForm.cancel")}
            </Button>
          )}
          <Button
            className="mb-4 sm:mb-0"
            type="submit"
            disabled={!form.formState.isDirty}
          >
            {isEdit
              ? form.formState.isDirty
                ? t("congressForm.save")
                : t("congressForm.saved")
              : t("congressForm.create")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
